/**
 * Project picker shown in the header of the requirement screens (SC-U15 /
 * SC-U16). The options come from `GET /projects`, so the list is already
 * limited to the projects the signed-in user may work in.
 */

import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuItem, TextField, makeStyles } from '@material-ui/core';
import { useActiveProject } from './ActiveProjectContext';

const useStyles = makeStyles({
  select: {
    minWidth: 240,
  },
});

export interface ProjectSelectorProps {
  /** Rendered disabled while a screen is mid-operation. */
  disabled?: boolean;
}

export const ProjectSelector: FC<ProjectSelectorProps> = ({ disabled }) => {
  const classes = useStyles();
  const { t } = useTranslation('projects');
  const { projects, activeProjectId, selectProject, status } =
    useActiveProject();

  // Nothing to pick between: the surrounding state message already explains it.
  if (status === 'loading' || status === 'error' || status === 'empty') {
    return null;
  }

  return (
    <TextField
      select
      size="small"
      variant="outlined"
      className={classes.select}
      label={t('activeProject.label')}
      value={activeProjectId ?? ''}
      disabled={disabled}
      onChange={event => {
        const raw = event.target.value;
        selectProject(raw === '' ? null : Number(raw));
      }}
      SelectProps={{
        // Without this the placeholder item renders as a blank row that looks
        // like a broken option rather than "nothing selected".
        displayEmpty: true,
      }}
      // `displayEmpty` means the control is never visually empty, but an
      // outlined label only floats by itself once there is a value — so with
      // nothing selected the label was drawn straight over the placeholder.
      // Pin it up: the field always shows text underneath it.
      InputLabelProps={{ shrink: true }}
      inputProps={{ 'aria-label': t('activeProject.label') }}
    >
      <MenuItem value="" disabled>
        {t('activeProject.placeholder')}
      </MenuItem>
      {projects.map(project => (
        <MenuItem key={project.id} value={project.id}>
          {project.name}
        </MenuItem>
      ))}
    </TextField>
  );
};

export default ProjectSelector;
